/**
 * Разметка сообщения.
 *
 * Свой разбор, а не библиотека: полный Markdown здесь вреден — таблицы и
 * картинки по ссылке в чате не нужны, а таскать ради шести знаков препинания
 * разборщик на сто килобайт тем более. Набор — тот же, что в Discord:
 * заголовки трёх уровней, подстрочник `-#` и ссылки с подписью там тоже есть.
 *
 * Упоминания разбираются **здесь же**, а не поверх. Иначе получилось бы два
 * разбора одного текста: первый режет строку на куски, второй ищет хэндлы в
 * тех кусках, что от него остались, — и `**@аня#0042**` перестаёт быть
 * упоминанием ровно потому, что попал внутрь жирного.
 */

import { stamp, type StampStyle } from "@shared/lib/format";

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "code"; text: string }
  | { kind: "strong"; parts: Inline[] }
  | { kind: "em"; parts: Inline[] }
  | { kind: "underline"; parts: Inline[] }
  | { kind: "strike"; parts: Inline[] }
  | { kind: "spoiler"; parts: Inline[] }
  | { kind: "mention"; handle: string }
  /** Зов всех: `@everyone` — кто видит канал, `@here` — из них кто в сети. */
  | { kind: "everyone"; word: "everyone" | "here" }
  /**
   * Упоминание канала. В тексте хранится номером: `<#…>`.
   *
   * Номером, а не именем, и это не прихоть. Имя канала меняют, и оно теперь
   * даже знаки допускает; сохрани мы его — упоминание после переименования
   * указывало бы в никуда, а таких в переписке накапливаются сотни. По номеру
   * же строка сама показывает свежее имя.
   */
  | { kind: "channel"; id: string }
  /**
   * Упоминание роли. В тексте хранится номером: `<@&…>`.
   *
   * Номером по той же причине, что и канал: имя роли меняют, и сохранённое
   * имя однажды указало бы в никуда. Показывается при этом имя — свежее.
   *
   * Начало записи своё, не `<#`: роль и канал зовут одинаково выглядящим
   * знаком, но ведут они в разные места, и разбирать их одним видом значило
   * бы искать канал по номеру роли.
   */
  | { kind: "role"; id: string }
  /** Кастомный знак сервера: в тексте он хранится именем в двоеточиях. */
  | { kind: "emoji"; name: string }
  /** Метка времени `<t:секунды:вид>` — как в Discord, в поясе читающего. */
  | { kind: "time"; at: Date; style: StampStyle }
  /** `label` — ссылка с подписью `[текст](адрес)`; нет — адрес и есть подпись. */
  | { kind: "link"; href: string; label?: Inline[] };

export type Block =
  | { kind: "paragraph"; parts: Inline[] }
  /** `# `, `## `, `### ` в начале строки — как в Discord: без пробела это не заголовок. */
  | { kind: "heading"; level: 1 | 2 | 3; parts: Inline[] }
  /** `-# ` — подстрочник, мелкий бледный текст. */
  | { kind: "subtext"; parts: Inline[] }
  | { kind: "quote"; blocks: Block[] }
  /**
   * Список. Пункт — свои блоки, а не строка: под текстом пункта бывает
   * вложенный список. `start` — номер первого пункта; у маркированного он 1
   * и не нужен.
   */
  | { kind: "list"; ordered: boolean; start: number; items: Block[][] }
  | { kind: "code"; lang: string | null; text: string };

/** Хэндл в тексте: тот же вид, по которому упоминание разбирает сервер. */
const HANDLE = /@([\p{L}\p{N}_.-]+)#(\d{4})(?!\d)/u;

/** `@everyone` и `@here` отдельным словом: хвост имени — не зов, точка конца предложения — зов. */
const EVERYONE = /@(everyone|here)(?![\p{L}\p{N}_#-])/u;

/**
 * Упоминание канала: `<#номер>`.
 *
 * Угловые скобки, а не голая решётка: `#` в переписке встречается сплошь и
 * рядом — в хэштегах, в номерах и в самом хэндле (`@аня#0042`). Разбирать по
 * ней значило бы находить упоминания там, где их нет.
 *
 * Набирают при этом обычную решётку — вид с номером подставляет подсказка
 * поля ввода, человек его не пишет.
 */
const CHANNEL_REF = /<#([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})>/;

/**
 * Упоминание роли: `<@&номер>`.
 *
 * Тот же вид, по которому упоминание разбирает сервер (`mentions::roles`).
 * Набирают при этом обычную «собаку» — вид с номером подставляет подсказка
 * поля ввода, человек его не пишет.
 */
const ROLE_REF = /<@&([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})>/;

/**
 * Кастомный знак сервера: `:имя:`.
 *
 * Буквы, цифры и подчёркивание, от двух знаков. Одной буквы мало нарочно:
 * «с 10:30 до 11:30 у» — обычная строка, и превращать её середину в знак
 * нельзя. Русские буквы допущены: имена знаков заводят на своём языке.
 *
 * Правило одно на всё приложение — им же проверяется имя при загрузке и
 * ищется совпадение в подсказке поля ввода.
 */
export const CUSTOM_EMOJI = /:([\p{L}\p{N}_]{2,32}):/u;

/**
 * Метка времени: `<t:1730000000>` или `<t:1730000000:R>`. Секунды, как в
 * Discord, — метки оттуда вставляют как есть. Дата за пределами того, что
 * умеет `Date`, остаётся текстом.
 */
const TIME_REF = /<t:(-?\d{1,12})(?::([tTdDfFR]))?>/;

/**
 * Ссылка целиком, без попытки угадать конец по знакам препинания.
 *
 * Закрывающая скобка и точка на конце отрезаются отдельно: «(см. https://a.b)»
 * и «зайди на https://a.b.» — обычные предложения, и утаскивать в ссылку их
 * хвост нельзя.
 */
const LINK = /https?:\/\/[^\s<>()]+/;

/** Ссылка с подписью: `[текст](адрес)`. Подпись в одну строку и не пустая. */
const MASKED = /^\[([^\]\n]{1,256})\]\((https?:\/\/[^\s<>()]+)\)/;

/**
 * Подпись, похожая на адрес или домен. Такую ссылку, как и Discord, подписью
 * не показываем: «https://bank.ru», ведущая на чужой сайт, — обман.
 */
const LOOKS_LIKE_URL = /https?:\/\/|www\.|[\p{L}\p{N}-]+\.[\p{L}]{2,}/iu;

/**
 * Знак пункта — как в Discord: `-`, `*` или номер с точкой. `1)` и `+`
 * Discord списком не считает, и мы тоже: текст, принесённый оттуда, должен
 * выглядеть так же, как там, где его написали.
 */
const BULLET = String.raw`(?:[*-]|\d+\.)`;

/** Начало списка: отступ, знак, пробел и хоть что-то, кроме пробелов. */
const LIST_START = new RegExp(String.raw`^( *)(${BULLET}) +\S`);

/** Отступ, знак и пробелы за ним — то, что снимается со строки пункта. */
const ITEM_PREFIX = new RegExp(String.raw`^ *${BULLET} +`);

/**
 * Сколько списков вкладывается друг в друга. Столько же разбирает Discord:
 * двенадцатый уровень остаётся строкой с чёрточкой.
 */
const MAX_LISTS = 11;

/**
 * Что снимает обратная черта — как в Discord, знаки препинания.
 *
 * Кроме `@` и `<`: упоминание сервер находит в тексте сам, и `\@everyone`
 * всё равно позовёт всех. Показать такой зов текстом значило бы спрятать,
 * что он ушёл.
 */
const ESCAPABLE = "!\"#$%&'()*+,-./:;=>?[\\]^_`{|}~";

/** Парные обёртки, от длинных к коротким: `**` должно выиграть у `*`. */
const WRAPS: { open: string; kind: Extract<Inline, { parts: Inline[] }>["kind"] }[] = [
  { open: "```", kind: "strong" }, // сюда не доходит: блоки снимаются раньше
  { open: "**", kind: "strong" },
  { open: "__", kind: "underline" },
  { open: "~~", kind: "strike" },
  { open: "||", kind: "spoiler" },
  { open: "*", kind: "em" },
  { open: "_", kind: "em" },
];

export function parse(text: string): Block[] {
  return blocksOf(text, 0);
}

/**
 * `level` — в скольких пунктах списка лежит текст. Внутри пункта заголовков
 * нет, как в Discord, а глубже {@link MAX_LISTS} список не начинается.
 */
function blocksOf(text: string, level: number): Block[] {
  const lines = text.split("\n");
  const blocks: Block[] = [];

  let at = 0;

  while (at < lines.length) {
    const line = lines[at];

    // Блок кода: от ``` до ```. Незакрытый идёт до конца текста — иначе
    // половина сообщения осталась бы разметкой на виду.
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      const lang = fence[1] || null;
      const body: string[] = [];
      at += 1;

      while (at < lines.length && !/^```\s*$/.test(lines[at])) {
        body.push(lines[at]);
        at += 1;
      }

      at += 1; // закрывающая
      blocks.push({ kind: "code", lang, text: body.join("\n") });
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quoted: string[] = [];

      while (at < lines.length && /^>\s?/.test(lines[at])) {
        quoted.push(lines[at].replace(/^>\s?/, ""));
        at += 1;
      }

      blocks.push({ kind: "quote", blocks: blocksOf(quoted.join("\n"), level) });
      continue;
    }

    // Заголовок и подстрочник — по одной строке, как в Discord.
    const heading = level === 0 ? /^(#{1,3}) (.*\S.*)$/.exec(line) : null;
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1].length as 1 | 2 | 3, parts: inline(heading[2]) });
      at += 1;
      continue;
    }

    const subtext = /^-# (.*\S.*)$/.exec(line);
    if (subtext) {
      blocks.push({ kind: "subtext", parts: inline(subtext[1]) });
      at += 1;
      continue;
    }

    const start = level < MAX_LISTS ? LIST_START.exec(line) : null;

    if (start) {
      /*
       * Список идёт, пока строка начинается с пробела или с пункта на том же
       * отступе — правило Discord. Строка с краю без знака список кончает, а
       * строка с отступом — продолжение пункта или вложенный список.
       */
      const sibling = new RegExp(`^${start[1]}${BULLET} `);
      const taken = [line];
      at += 1;

      while (at < lines.length && (lines[at].startsWith(" ") || sibling.test(lines[at]))) {
        taken.push(lines[at]);
        at += 1;
      }

      blocks.push(list(taken, start[1].length, start[2], level));
      continue;
    }

    /*
     * Обычный текст: пустые строки сохраняются как есть — в переписке ими
     * разделяют мысль, и съедать их значит переписывать чужое сообщение.
     *
     * Первая строка забирается всегда, не глядя на признак начала блока.
     * Иначе строка, которая на блок похожа, но им не стала — скажем,
     * «```rust и ещё что-то», — не досталась бы ни одной ветке, и разбор
     * крутился бы на месте вечно. Вешало намертво и без единой ошибки.
     */
    const paragraph: string[] = [lines[at]];
    at += 1;

    while (at < lines.length && !isBlockStart(lines[at], level)) {
      paragraph.push(lines[at]);
      at += 1;
    }

    blocks.push({ kind: "paragraph", parts: inline(paragraph.join("\n")) });
  }

  return blocks;
}

function isBlockStart(line: string, level: number): boolean {
  return (
    /^```/.test(line) ||
    (level === 0 && /^#{1,3} .*\S/.test(line)) ||
    /^-# .*\S/.test(line) ||
    /^>\s?/.test(line) ||
    (level < MAX_LISTS && LIST_START.test(line))
  );
}

/**
 * Список из его строк — режется так же, как в Discord.
 *
 * Новый пункт — строка со знаком на отступе первого пункта или на пробел
 * дальше. Остальное — продолжение пункта: с него снимается столько пробелов,
 * сколько занимает знак первого пункта, и пункт разбирается заново. Так
 * «  - …» под «- …» становится вложенным списком, а строка с отступом без
 * знака — второй строкой пункта.
 *
 * Вид и номер начала задаёт первый пункт: «-» и «*» под «1.» — пункты того же
 * нумерованного списка, а номера дальше идут подряд, что бы в них ни стояло.
 */
function list(lines: string[], indent: number, bullet: string, level: number): Block {
  const head = new RegExp(`^ {${indent},${indent + 1}}${BULLET} `);
  const unindent = new RegExp(`^ {1,${ITEM_PREFIX.exec(lines[0])![0].length}}`);
  const items: string[][] = [];

  for (const line of lines) {
    if (head.test(line)) items.push([line.replace(ITEM_PREFIX, "")]);
    else items[items.length - 1].push(line.replace(unindent, ""));
  }

  const ordered = bullet !== "-" && bullet !== "*";

  return {
    kind: "list",
    ordered,
    // Ноль и запредельный номер Discord поджимает так же: от 1 до миллиарда.
    start: ordered ? Math.min(1e9, Math.max(1, Number.parseInt(bullet, 10))) : 1,
    items: items.map((item) => blocksOf(item.join("\n").trimEnd(), level + 1)),
  };
}

/**
 * Разбор внутри строки.
 *
 * Порядок важен: `код` снимается первым и внутри себя не разбирается вовсе —
 * иначе `` `**` `` перестало бы быть примером и стало разметкой.
 */
export function inline(text: string): Inline[] {
  const parts: Inline[] = [];
  let plain = "";

  const flush = () => {
    if (plain) parts.push({ kind: "text", text: plain });
    plain = "";
  };

  let at = 0;

  while (at < text.length) {
    const rest = text.slice(at);

    // Экранирование: `\*` оставляет звёздочку звёздочкой, `1\.` — номер текстом.
    if (rest[0] === "\\" && rest.length > 1 && ESCAPABLE.includes(rest[1])) {
      plain += rest[1];
      at += 2;
      continue;
    }

    if (rest[0] === "`") {
      const end = rest.indexOf("`", 1);
      if (end > 1) {
        flush();
        parts.push({ kind: "code", text: rest.slice(1, end) });
        at += end + 1;
        continue;
      }
    }

    const wrap = WRAPS.find(
      (candidate) =>
        candidate.open !== "```" &&
        rest.startsWith(candidate.open) &&
        closingAt(rest, candidate.open) > 0,
    );

    if (wrap) {
      const end = closingAt(rest, wrap.open);
      flush();
      parts.push({
        kind: wrap.kind,
        parts: inline(rest.slice(wrap.open.length, end)),
      } as Inline);
      at += end + wrap.open.length;
      continue;
    }

    /*
     * Роль — прежде хэндла: у них общее начало, и `@` из `<@&…>` иначе
     * попал бы в разбор имени. Совпасть с хэндлом номер роли не может — там
     * решётка и четыре цифры, — но порядок всё равно однозначнее проверки.
     */
    const role = ROLE_REF.exec(rest);
    if (role?.index === 0) {
      flush();
      parts.push({ kind: "role", id: role[1] });
      at += role[0].length;
      continue;
    }

    const everyone = EVERYONE.exec(rest);
    if (everyone?.index === 0 && (at === 0 || !/[\p{L}\p{N}_.-]/u.test(text[at - 1]))) {
      flush();
      parts.push({ kind: "everyone", word: everyone[1] as "everyone" | "here" });
      at += everyone[0].length;
      continue;
    }

    const handle = HANDLE.exec(rest);
    if (handle?.index === 0) {
      flush();
      parts.push({ kind: "mention", handle: `${handle[1]}#${handle[2]}` });
      at += handle[0].length;
      continue;
    }

    // Здесь же, а не поверх, по той же причине: иначе `**<#…>**` перестало бы
    // быть упоминанием, попав внутрь жирного.
    const channel = CHANNEL_REF.exec(rest);
    if (channel?.index === 0) {
      flush();
      parts.push({ kind: "channel", id: channel[1] });
      at += channel[0].length;
      continue;
    }

    /*
     * Кастомный знак сервера. Разбирается здесь, а не поверх, ровно по той
     * же причине, что и упоминание: иначе `**:горит:**` перестало бы быть
     * знаком, попав внутрь жирного.
     *
     * Известен ли знак — решает показ: тут мы только видим, что человек
     * назвал знак по имени. Незнакомое имя остаётся именем в рамке, а не
     * пропадает.
     */
    const custom = CUSTOM_EMOJI.exec(rest);
    if (custom?.index === 0) {
      flush();
      parts.push({ kind: "emoji", name: custom[1] });
      at += custom[0].length;
      continue;
    }

    const time = TIME_REF.exec(rest);
    const moment = time?.index === 0 ? new Date(Number(time[1]) * 1000) : null;
    if (time && moment && !Number.isNaN(moment.getTime())) {
      flush();
      parts.push({ kind: "time", at: moment, style: (time[2] ?? "f") as StampStyle });
      at += time[0].length;
      continue;
    }

    /*
     * Подпись разбирается как обычный текст, только без ссылок внутри: ссылка
     * в ссылке не открывается ни так, ни эдак.
     */
    const masked = rest[0] === "[" ? MASKED.exec(rest) : null;
    if (masked && !LOOKS_LIKE_URL.test(masked[1])) {
      flush();
      parts.push({ kind: "link", href: masked[2], label: inline(masked[1]) });
      at += masked[0].length;
      continue;
    }

    const link = LINK.exec(rest);
    if (link?.index === 0) {
      flush();
      parts.push({ kind: "link", href: trimTail(link[0]) });
      at += trimTail(link[0]).length;
      continue;
    }

    plain += rest[0];
    at += 1;
  }

  flush();

  return parts;
}

/**
 * Где закрывается обёртка. `0` — не закрывается вовсе, и тогда это просто
 * текст: незакрытая звёздочка встречается в живой речи чаще, чем разметка.
 */
function closingAt(rest: string, open: string): number {
  const from = open.length;
  if (rest.length <= from) return 0;

  const end = rest.indexOf(open, from);

  // Пустая обёртка (`****`) разметкой не считается.
  return end > from ? end : 0;
}

/** Отрезает у ссылки хвост, который на деле принадлежит предложению. */
function trimTail(href: string): string {
  return href.replace(/[.,;:!?]+$/, "");
}

/** Имена по номерам — где справочник есть, как у имени ветки в канале. */
export interface PlainNames {
  channelOf?: (id: string) => string | null;
  roleOf?: (id: string) => string | null;
  /**
   * Метку «R» оставить относительной («через 3 минуты»), как она видна в
   * ленте. Только для подсказки имени на шаге «Новая ветка»: она станет
   * именем текстом на момент создания. Везде, где текст живёт дольше, —
   * оповещение, имя готовой ветки, — метка датой: иначе застыла бы или
   * тикала.
   */
  live?: boolean;
}

/**
 * Текст без разметки — для тех мест, где показать её нечем.
 *
 * Системное оповещение и цитата ответа рисуются одной строкой без стилей, и
 * `**срочно**` в них читается хуже, чем просто «срочно». Имя ветки, взятое
 * из начала корня, — тоже: метка времени в нём читается датой, как в Discord.
 */
export function plainText(text: string, names: PlainNames = {}): string {
  return parse(text)
    .map((block) => blockText(block, names))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function blockText(block: Block, names: PlainNames): string {
  switch (block.kind) {
    case "code":
      return block.text;
    case "quote":
      return block.blocks.map((inner) => blockText(inner, names)).join(" ");
    case "list":
      return block.items.flat().map((inner) => blockText(inner, names)).join(" ");
    default:
      return inlineText(block.parts, names);
  }
}

function inlineText(parts: Inline[], names: PlainNames): string {
  return parts
    .map((part) => {
      switch (part.kind) {
        case "text":
        case "code":
          return part.text;
        case "mention":
          return `@${part.handle.split("#")[0]}`;

        /*
         * Без справочника имени взять негде — так набирают предпросмотр
         * ответа и уведомление. Слово честнее номера, который человеку ничего
         * не скажет.
         */
        case "channel":
          return `#${names.channelOf?.(part.id) ?? "канал"}`;
        // И роль по той же причине.
        case "role":
          return `@${names.roleOf?.(part.id) ?? "роль"}`;
        case "everyone":
          return `@${part.word}`;
        // Знак сервера остаётся именем: показать его картинкой в оповещении
        // всё равно нечем, а имя хотя бы читается.
        case "emoji":
          return `:${part.name}:`;
        // «Через 2 часа» в оповещении застыло бы, а дата верна всегда.
        case "time":
          return stamp(part.at, part.style === "R" && !names.live ? "f" : part.style);
        case "link":
          return part.label ? inlineText(part.label, names) : part.href;
        default:
          return inlineText(part.parts, names);
      }
    })
    .join("");
}

/**
 * Ссылки для превью под сообщением — как в Discord, не больше трёх и без
 * повторов. Ссылка в коде — это код, а не приглашение открыть; под спойлером —
 * спрятана нарочно, и карточка выдала бы её.
 */
export function links(text: string, limit = 3): string[] {
  const found: string[] = [];

  const walk = (parts: Inline[]) => {
    for (const part of parts) {
      if (found.length >= limit) return;
      if (part.kind === "link") {
        if (!found.includes(part.href)) found.push(part.href);
      } else if (part.kind !== "spoiler" && "parts" in part) {
        walk(part.parts);
      }
    }
  };
  const visit = (blocks: Block[]) => {
    for (const block of blocks) {
      if (block.kind === "paragraph" || block.kind === "heading" || block.kind === "subtext") walk(block.parts);
      else if (block.kind === "quote") visit(block.blocks);
      else if (block.kind === "list") block.items.forEach(visit);
    }
  };

  visit(parse(text));
  return found;
}

/**
 * Названы ли мы в этом тексте — сами или через свою роль.
 *
 * Роли отдельным доводом, а не собранными из текста: в тексте лежит номер
 * роли, а свои ли это роли, знает только тот, кто спрашивает.
 *
 * Роль по умолчанию сюда попасть не должна: она есть у всех, и с ней
 * подсветилось бы каждое сообщение, где кто-то назвал кого угодно. Отсекает
 * её тот, кто собирает список, — состав сервера роль по умолчанию человеку и
 * не приписывает.
 */
export function mentionsMe(text: string, mine: string, roles: readonly string[] = []): boolean {
  if (text.includes(`@${mine}`)) return true;

  return roles.some((id) => text.includes(`<@&${id}>`));
}

/** `@silent` в начале — как в Discord: само слово уходит, остаётся признак. */
const SILENT = /^@silent(?:\s+|$)/;

/** Текст без `@silent` и признак тихого сообщения: получателей оно не будит. */
export function silentPrefix(content: string): { content: string; silent: boolean } {
  return SILENT.test(content) ? { content: content.replace(SILENT, ""), silent: true } : { content, silent: false };
}
